<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { McInput, McTooltip } from "@maersk-global/mds-components-core";
import { useFiltersStore } from "@/store";
import { FilterType, FilterTypeEnum, useApplyFilter, FilterDefinition } from "@/features/filters";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  filter: FilterDefinition;
  filterType: FilterType;
  zonePrefix?: string;
}>();

const {
  key: filterKey,
  label: filterLabel = "",
  placeholder = "",
  hiddenlabel,
  sectionLabel,
  groupId,
  groupLabel,
  clearButton,
  inputIcon,
  labelTooltip = false
} = props.filter;

const { t } = useI18n();

const zone = props.zonePrefix || "default";
const filtersStore = useFiltersStore();
const { filtersApplied, panelSelectedFilters } = filtersStore;
const { applyFilter, removeFilter } = useApplyFilter(props.filterType, props.zonePrefix);

const typedValue = ref("");
const hasUserCleared = ref(false);

const selectedFilter = computed(() => {
  const source = props.filterType === FilterTypeEnum.PANEL_FILTERS ? panelSelectedFilters : filtersApplied;
  const storeValue = source?.[zone]?.[filterKey];
  return {
    ...(storeValue || { label: "", value: "" }),
    filterType: props.filterType
  };
});

const inputValue = computed(() => {
  if (hasUserCleared.value) return "";
  if (typedValue.value !== "") return typedValue.value;
  return selectedFilter.value?.label || "";
});

watch(
  selectedFilter,
  (val) => {
    if (!hasUserCleared.value) {
      typedValue.value = val?.value || "";
    }
    if (!val?.value) {
      hasUserCleared.value = false;
    }
  },
  { immediate: true }
);

const updatePanelFilters = (filterData) => {
  if (!panelSelectedFilters[zone]) panelSelectedFilters[zone] = {};
  if (filterData) {
    panelSelectedFilters[zone][filterKey] = filterData;
  } else {
    delete panelSelectedFilters[zone][filterKey];
  }
};

const updateAppliedFilters = (filterData) => {
  if (filterData) {
    applyFilter(filterKey, filterData);
  } else {
    removeFilter(filterKey);
  }
};

const onInput = (e: Event) => {
  const text = (e.target as HTMLInputElement).value || "";
  typedValue.value = text;
  hasUserCleared.value = text.trim() === "";

  const trimmed = text.trim();

  const filterData = trimmed
    ? {
        filterKey,
        label: trimmed,
        value: trimmed,
        filterType: props.filterType,
        ...(groupId ? { groupId } : {}),
        ...(groupLabel ? { groupLabel } : {}),
        ...(sectionLabel ? { sectionLabel } : {}),
        ...(filterLabel ? { filterLabel } : {})
      }
    : null;

  if (props.filterType === FilterTypeEnum.PANEL_FILTERS) {
    updatePanelFilters(filterData);
  } else {
    updateAppliedFilters(filterData);
  }
};

const onClear = () => {
  typedValue.value = "";
  hasUserCleared.value = true;

  removeFilter(filterKey);
};
</script>

<template>
  <mc-tooltip fit="small">
    <mc-input
      slot="trigger"
      :name="filterKey"
      fit="small"
      :label="t(filterLabel)"
      :placeholder="t(placeholder)"
      :hiddenlabel="hiddenlabel"
      :trailingicon="inputIcon"
      :clearbutton="clearButton"
      :value="inputValue"
      @input="onInput"
      @clearbuttonclick="onClear"
    />
    <span v-if="labelTooltip && inputValue">{{ t(filterLabel) }}</span>
  </mc-tooltip>
</template>
